import React, { useEffect, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import PeopleIcon from '@mui/icons-material/People';
// Deprecated modules:
// import SchoolIcon from '@mui/icons-material/School';
// Deprecated modules:
// import BuildIcon from '@mui/icons-material/Build';
import CalculateIcon from '@mui/icons-material/Calculate';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
// import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import TrafficIcon from '@mui/icons-material/Traffic';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { usePermissions } from '../hooks/usePermissions';

interface AppSwitcherProps {
  open: boolean;
  onClose: () => void;
}

const AppSwitcher: React.FC<AppSwitcherProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const {
    canAccessPrerequisites,
    canAccessAttendance,
    // Deprecated modules:
    // canAccessCourses,
    canAccessCourseBuilder,
    canAccessTempCards,
    canAccessTestSheetSubmissions,
    canAccessDOBVerifier,
    canManageUsers,
    isAdmin,
  } = usePermissions();

  console.log('AppSwitcher rendered with open:', open);

  const canAccessAdminTools = () => canManageUsers() || isAdmin();

  const allApps = [
    {
      id: 'prerequisites',
      name: 'Check Prerequisites',
      description: 'Award Checker',
      path: '/award-check',
      icon: <CheckCircleOutlinedIcon sx={{ fontSize: 20 }} />,
      color: '#1976d2',
      permission: canAccessPrerequisites(),
    },
    {
      id: 'attendance',
      name: 'Attendance',
      description: 'Manage Student Attendance',
      path: '/attendance',
      icon: <PeopleIcon sx={{ fontSize: 20 }} />,
      color: '#388e3c',
      permission: canAccessAttendance(),
    },
    /* Deprecated: Verify Posted Courses
    {
      id: 'courses',
      name: 'Verify Posted Courses',
      description: 'Course Scraper',
      path: '/course-scraper',
      icon: <SchoolIcon sx={{ fontSize: 20 }} />,
      color: '#f57c00',
      permission: canAccessCourses(),
    },
    */
    /* Deprecated: Course Builder
    {
      id: 'course-builder',
      name: 'Course Builder',
      description: 'Create Classrooms & Courses',
      path: '/course-upload',
      icon: <BuildIcon sx={{ fontSize: 20 }} />,
      color: '#ed6c02',
      permission: canAccessCourseBuilder(),
    },
    */
    {
      id: 'course-profitability',
      name: 'Course Profitability',
      description: 'Decision Tree & Profit Calculator',
      path: '/course-profitability',
      icon: <CalculateIcon sx={{ fontSize: 20 }} />,
      color: '#00897b',
      permission: canAccessCourseBuilder(),
    },
    {
      id: 'connecteam-scheduler',
      name: 'ConnectTeam Scheduler',
      description: 'Create Shifts in ConnectTeam',
      path: '/connecteam-scheduler',
      icon: <ScheduleIcon sx={{ fontSize: 20 }} />,
      color: '#7b1fa2',
      permission: canAccessCourseBuilder(),
    },
    {
      id: 'temp-cards',
      name: 'Temp Cards',
      description: 'Generate Temporary Certification Cards',
      path: '/temp-cards',
      icon: <CreditCardIcon sx={{ fontSize: 20 }} />,
      color: '#0288d1',
      permission: canAccessTempCards(),
    },
    {
      id: 'test-sheet-submissions',
      name: 'Test Sheet Submissions',
      description: 'Monitor Test Sheet Email Submissions',
      path: '/test-sheet-submissions',
      icon: <EmailIcon sx={{ fontSize: 20 }} />,
      color: '#9c27b0',
      permission: canAccessTestSheetSubmissions(),
    },
    {
      id: 'dob-verifier',
      name: 'DOB Verifier',
      description: 'Verify Student Dates of Birth',
      path: '/dob-verifier',
      icon: <CalendarTodayIcon sx={{ fontSize: 20 }} />,
      color: '#d32f2f',
      permission: canAccessDOBVerifier(),
    },
    /* Deprecated: Object Purge
    {
      id: 'object-purge',
      name: 'Object Purge',
      description: '⚠️ Delete ALL Records from Object',
      path: '/object-purge',
      icon: <DeleteForeverIcon sx={{ fontSize: 20 }} />,
      color: '#b71c1c', // Dark red to indicate danger
      permission: canManageUsers(), // Admin-only access
    },
    */
    {
      id: 'road-closures',
      name: 'Road Closures',
      description: 'Weekend Travel Highlights',
      path: '/road-closures',
      icon: <TrafficIcon sx={{ fontSize: 20 }} />,
      color: '#ef6c00',
      permission: canManageUsers(), // Admin-only access
    },
    {
      id: 'company-policies',
      name: 'Company Policies',
      description: 'Search Policies & Knowledge Graph',
      path: '/company-policies',
      icon: <MenuBookIcon sx={{ fontSize: 20 }} />,
      color: '#27445C',
      permission: canManageUsers(), // Admin-only access
    },
    {
      id: 'lss-invoices',
      name: 'LSS Invoices',
      description: 'Outstanding Invoice Monitor',
      path: '/lss-invoices',
      icon: <ReceiptLongIcon sx={{ fontSize: 20 }} />,
      color: '#546e7a',
      permission: canAccessAdminTools(),
    },
  ];

  // Filter apps based on user permissions
  const apps = allApps.filter(app => app.permission);

  const getCurrentAppId = () => {
    const path = location.pathname;
    if (path.includes('award-check')) return 'prerequisites';
    if (path.includes('attendance')) return 'attendance';
    // Deprecated modules:
    // if (path.includes('course-scraper')) return 'courses';
    // if (path.includes('course-upload')) return 'course-builder';
    if (path.includes('course-profitability')) return 'course-profitability';
    if (path.includes('connecteam-scheduler')) return 'connecteam-scheduler';
    if (path.includes('temp-cards')) return 'temp-cards';
    if (path.includes('test-sheet-submissions')) return 'test-sheet-submissions';
    if (path.includes('dob-verifier')) return 'dob-verifier';
    // if (path.includes('object-purge')) return 'object-purge';
    if (path.includes('road-closures')) return 'road-closures';
    if (path.includes('company-policies')) return 'company-policies';
    if (path.includes('lss-invoices')) return 'lss-invoices';
    return null;
  };

  const currentAppId = getCurrentAppId();

  const handleAppClick = (path: string) => {
    onClose();
    navigate(path);
  };

  // Animation effects
  useEffect(() => {
    if (!containerRef.current || !buttonsRef.current) return;

    if (open) {
      // Slide down animation
      gsap.fromTo(
        containerRef.current,
        {
          y: -20,
          opacity: 0,
          scaleY: 0,
          transformOrigin: 'top center',
        },
        {
          y: 0,
          opacity: 1,
          scaleY: 1,
          duration: 0.4,
          ease: 'back.out(1.7)',
        }
      );

      // Stagger animation for buttons
      const buttons = buttonsRef.current.children;
      gsap.set(buttons, { opacity: 0, y: 10 });
      gsap.to(buttons, {
        opacity: 1,
        y: 0,
        duration: 0.3,
        stagger: 0.1,
        delay: 0.2,
        ease: 'power2.out',
      });
    }
  }, [open]);

  // Click outside to close functionality
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (open && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Also check if the click was on the app switcher button in the navbar
        const appSwitcherButton = document.querySelector('[data-app-switcher-button]');
        if (appSwitcherButton && !appSwitcherButton.contains(event.target as Node)) {
          onClose();
        }
      }
    };

    if (open) {
      // Add delay to prevent immediate close on open
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [open, onClose]);

  // Close on route change
  useEffect(() => {
    if (open) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ESC key to close functionality
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (open && event.key === 'Escape') {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        zIndex: 9999,
        background: 'rgba(156, 172, 198, 0.36)', // Sea glass translucent background
        backdropFilter: 'blur(10px)', // Frosted glass effect
        WebkitBackdropFilter: 'blur(10px)', // Safari support
        borderRadius: '0 0 12px 12px',
        boxShadow: '0 8px 32px rgba(176, 224, 230, 0.2)', // Sea glass shadow
        border: '1px solid rgba(176, 224, 230, 0.3)', // Sea glass border
        borderTop: 'none',
        overflow: 'hidden',
      }}
    >
      <Box
        ref={buttonsRef}
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {apps.map((app) => {
          const isCurrent = currentAppId === app.id;
          
          return (
            <Button
              key={app.id}
              onClick={() => handleAppClick(app.path)}
              disabled={isCurrent}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 2,
                p: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                backgroundColor: isCurrent ? 'primary.main' : 'transparent',
                color: isCurrent ? 'primary.contrastText' : 'text.primary',
                '&:hover': {
                  backgroundColor: isCurrent ? 'primary.main' : 'action.hover',
                  transform: isCurrent ? 'none' : 'translateX(4px)',
                },
                '&:disabled': {
                  color: 'primary.contrastText',
                  opacity: 0.9,
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  backgroundColor: isCurrent ? 'rgba(255,255,255,0.2)' : `${app.color}20`,
                  color: isCurrent ? 'inherit' : app.color,
                }}
              >
                {app.icon}
              </Box>
              <Box sx={{ flex: 1, textAlign: 'left' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {app.name}
                  {isCurrent && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        ml: 1,
                        px: 1,
                        py: 0.25,
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        borderRadius: 1,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                      }}
                    >
                      CURRENT
                    </Typography>
                  )}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {app.description}
                </Typography>
              </Box>
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default AppSwitcher; 
